<template>
  <!--
    Moving an order (AB#21303).

    Two full-width rows — a status select and a reason box with a Move
    button — sat under the stage strip on every order, whether or not
    anyone meant to move it. Now there is one line: a "Move order" button
    that says where the order is and what comes next, and the form only
    appears when it is pressed. The picker groups the statuses under the
    stages of the strip above it, greys the current one, highlights the
    natural next one, and keeps the reason in the same popover, so a move
    is one gesture instead of three controls.

    The status and reason are the parent's refs (v-model), and the move is
    the parent's — this decides nothing about what a move does.
  -->
  <div class="mvp" ref="root">
    <div class="mvp-row">
      <button
        type="button"
        class="mvp-open"
        :class="{ 'mvp-open--on': open }"
        :disabled="disabled || busy"
        :title="disabled ? disabledReason : 'Move this order to another status'"
        :aria-expanded="open"
        aria-haspopup="dialog"
        data-test="mvp-open"
        @click="toggle"
      >
        <ArrowRightCircleIcon class="mvp-open-icon" aria-hidden="true" />
        <span v-if="busy">Moving…</span>
        <span v-else-if="status">Move to {{ selectedText }}</span>
        <span v-else>Move order</span>
        <ChevronDownIcon class="mvp-chev" aria-hidden="true" />
      </button>
      <span class="mvp-hint" data-test="mvp-hint">
        <template v-if="disabled">{{ disabledReason }}</template>
        <template v-else>
          Currently <b>{{ currentText }}</b>
          <template v-if="nextOption"> · next is <b>{{ nextOption.text }}</b></template>
        </template>
      </span>
    </div>

    <div v-if="open" class="mvp-pop" role="dialog" :aria-label="`Move order ${orderNo} to`" data-test="mvp-pop">
      <div class="mvp-pop-h">Move order #{{ orderNo }} to</div>
      <div class="mvp-groups">
        <div v-for="group in groups" :key="group.title" class="mvp-group">
          <div class="mvp-group-t">{{ group.title }}</div>
          <button
            v-for="opt in group.options"
            :key="opt.value"
            type="button"
            class="mvp-item"
            :class="{
              'mvp-item--on': opt.value === status,
              'mvp-item--current': opt.value === currentStatus,
              'mvp-item--next': nextOption && opt.value === nextOption.value && opt.value !== status,
              'mvp-item--danger': opt.value === 'order_cancel',
            }"
            :data-test="`mvp-opt-${opt.value}`"
            @click="pick(opt.value)"
          >
            {{ opt.text }}
            <i v-if="opt.value === currentStatus">current</i>
            <i v-else-if="nextOption && opt.value === nextOption.value">next</i>
          </button>
        </div>
      </div>
      <div class="mvp-foot">
        <input
          ref="reasonEl"
          :value="reason"
          type="text"
          class="mvp-reason"
          placeholder="Reason (required)"
          data-test="mvp-reason"
          :disabled="busy"
          @input="emit('update:reason', ($event.target as HTMLInputElement).value)"
          @keydown.enter.prevent="submit"
        />
        <button type="button" class="mvp-go" data-test="mvp-move" :disabled="!canMove" @click="submit">
          {{ busy ? 'Moving…' : status ? `Move to ${selectedText}` : 'Move' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ArrowRightCircleIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

export interface MoveOption {
  value: string;
  text: string;
}

const props = withDefaults(
  defineProps<{
    options: MoveOption[];
    /** The order item's current status key. */
    currentStatus: string;
    /** That status in words, for when it is not one of the options (on hold). */
    currentStatusText?: string;
    orderNo: string | number;
    status: string | null;
    reason: string;
    disabled?: boolean;
    disabledReason?: string;
    busy?: boolean;
  }>(),
  { currentStatusText: '', disabled: false, disabledReason: '', busy: false }
);

const emit = defineEmits<{
  (e: 'update:status', value: string | null): void;
  (e: 'update:reason', value: string): void;
  (e: 'move'): void;
}>();

/**
 * Which stage of the strip each status belongs under. Anything not listed
 * (a status added later) lands in "Other" rather than vanishing.
 */
const STAGE_OF: Record<string, string> = {
  submitted_for_factory_review: 'Order',
  order_approve: 'Order',
  order_cancel: 'Order',
  factory_approved: 'Artwork',
  factory_approve_reject: 'Artwork',
  submitted_for_customer_review: 'Sample',
  in_production: 'Production',
  production_files_uploaded: 'Production',
  quality_control: 'Production',
  shipped: 'Shipping',
  completed: 'Shipping',
};
const STAGE_ORDER = ['Order', 'Artwork', 'Sample', 'Production', 'Shipping', 'Other'];

const groups = computed(() => {
  const byStage = new Map<string, MoveOption[]>();
  for (const opt of props.options) {
    const stage = STAGE_OF[opt.value] ?? 'Other';
    if (!byStage.has(stage)) byStage.set(stage, []);
    byStage.get(stage)!.push(opt);
  }
  return STAGE_ORDER.filter((s) => byStage.has(s)).map((title) => ({ title, options: byStage.get(title)! }));
});

const optionFor = (value: string | null | undefined) => props.options.find((o) => o.value === value) ?? null;

const currentText = computed(
  () => optionFor(props.currentStatus)?.text || props.currentStatusText || props.currentStatus || '—'
);
const selectedText = computed(() => optionFor(props.status)?.text ?? '');

/**
 * The natural next status: the one after the current in the order's
 * progression — not in the options list, which has permission-gated
 * entries appended at its end. Cancelling and the artwork re-run are never
 * "next". Null when the order is at the end or its status is not a stage.
 */
const PROGRESSION = [
  'submitted_for_factory_review',
  'order_approve',
  'factory_approved',
  'submitted_for_customer_review',
  'production_files_uploaded',
  'in_production',
  'quality_control',
  'shipped',
  'completed',
];
const nextOption = computed<MoveOption | null>(() => {
  const idx = PROGRESSION.indexOf(props.currentStatus);
  if (idx < 0) return null;
  for (let i = idx + 1; i < PROGRESSION.length; i++) {
    const opt = optionFor(PROGRESSION[i]);
    if (opt) return opt;
  }
  return null;
});

const canMove = computed(() => !props.busy && !!props.status && props.reason.trim() !== '');

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const reasonEl = ref<HTMLInputElement | null>(null);

const toggle = () => {
  open.value = !open.value;
};
const pick = async (value: string) => {
  emit('update:status', value);
  await nextTick();
  reasonEl.value?.focus();
};
const submit = () => {
  if (!canMove.value) return;
  open.value = false;
  emit('move');
};

const onDocClick = (e: MouseEvent) => {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false;
};
const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && open.value) open.value = false;
};
onMounted(() => {
  document.addEventListener('mousedown', onDocClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocClick);
  document.removeEventListener('keydown', onKey);
});
</script>

<style lang="scss">
/* px on purpose: the root font is 12.8px. Unscoped: nothing here is generic. */
.mvp {
  @apply relative mt-[12px];
}
.mvp-row {
  @apply flex flex-wrap items-center gap-[12px];
}
.mvp-open {
  @apply inline-flex items-center gap-[6px] h-[34px] px-[14px] rounded-[6px] border border-[var(--accent)] bg-surface
         text-[12.5px] font-semibold text-[var(--accent)] cursor-pointer transition-colors;
}
.mvp-open:hover:not(:disabled),
.mvp-open--on {
  @apply bg-[var(--accent-soft)];
}
.mvp-open:disabled {
  @apply opacity-50 cursor-not-allowed;
}
.mvp-open-icon {
  @apply w-[16px] h-[16px];
}
.mvp-chev {
  @apply w-[14px] h-[14px];
}
.mvp-hint {
  @apply text-[12px] text-ink-soft;
}
.mvp-hint b {
  @apply font-semibold text-ink;
}
.mvp-pop {
  @apply absolute left-0 z-40 mt-[8px] w-full max-w-[860px] bg-surface border border-line rounded-[10px] p-[14px];
  box-shadow: 0 8px 24px rgba(28, 31, 38, 0.12);
}
.mvp-pop-h {
  @apply text-[12.5px] font-semibold text-ink mb-[8px];
}
.mvp-groups {
  @apply grid gap-[10px];
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}
.mvp-group-t {
  @apply text-[10.5px] uppercase tracking-[0.06em] text-ink-mute font-semibold mb-[4px] px-[8px];
}
.mvp-item {
  @apply block w-full text-left text-[12.5px] px-[8px] py-[5px] rounded-[6px] text-ink bg-transparent border-0 cursor-pointer;
}
.mvp-item:hover {
  @apply bg-[#f3f4f6];
}
.mvp-item i {
  @apply not-italic text-[10.5px] text-ink-mute ml-[6px];
}
.mvp-item--current {
  @apply text-ink-mute;
}
.mvp-item--next {
  @apply bg-[var(--accent-soft)] text-[var(--accent)] font-semibold;
}
.mvp-item--next i {
  @apply text-[var(--accent)];
}
.mvp-item--danger {
  @apply text-[#b42318];
}
.mvp-item--on,
.mvp-item--on:hover {
  @apply bg-[var(--accent)] text-white font-semibold;
}
.mvp-item--on i {
  @apply text-white;
}
.mvp-foot {
  @apply flex flex-wrap gap-[8px] mt-[12px] pt-[12px] border-t border-[#eef0f3];
}
.mvp-reason {
  @apply flex-1 min-w-[200px] border border-line rounded-[6px] px-[12px] py-[8px] text-[12.5px] outline-none;
}
.mvp-reason:focus {
  @apply border-[var(--accent)];
}
.mvp-go {
  @apply bg-ink text-surface border-0 rounded-[6px] px-[16px] py-[8px] text-[12.5px] font-medium cursor-pointer whitespace-nowrap;
}
.mvp-go:disabled {
  @apply opacity-50 cursor-not-allowed bg-gray-600;
}
</style>
